// src/services/api.js
// Couche de données SOLSTICE — remplace 100% des appels localStorage
import { supabase } from '../supabase'

// ── Helpers ───────────────────────────────────────────────────────────────────

const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

const log = async (action, table, recordId, oldData = null, newData = null) => {
  const user = await getCurrentUser()
  if (!user) return
  await supabase.from('audit_log').insert({
    user_id:    user.id,
    user_email: user.email,
    action,
    table_name: table,
    record_id:  String(recordId),
    old_data:   oldData,
    new_data:   newData,
  })
}

export const genererNumero = async (type) => {
  const { data, error } = await supabase.rpc('get_next_numero', { p_type: type })
  if (error) throw error
  return data
}

// ── Documents ─────────────────────────────────────────────────────────────────

export const documentsApi = {

  async liste({ page = 1, perPage = 10, type, statut, clientId,
                search, dateDebut, dateFin, montantMin, montantMax } = {}) {
    let q = supabase
      .from('documents')
      .select('*, client:clients(nom, email, telephone)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range((page - 1) * perPage, page * perPage - 1)

    if (type      && type !== 'all')    q = q.eq('type', type)
    if (statut    && statut !== 'all')  q = q.eq('statut', statut)
    if (clientId)                       q = q.eq('client_id', clientId)
    if (dateDebut)                      q = q.gte('date', dateDebut)
    if (dateFin)                        q = q.lte('date', dateFin)
    if (montantMin)                     q = q.gte('total', montantMin)
    if (montantMax)                     q = q.lte('total', montantMax)
    if (search)   q = q.or(`numero.ilike.%${search}%,objet.ilike.%${search}%`)

    const { data, error, count } = await q
    if (error) throw error
    return { data: data || [], count: count || 0 }
  },

  async get(id) {
    const { data, error } = await supabase
      .from('documents')
      .select('*, client:clients(*), createur:profils!created_by(nom, prenom, email)')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  async create(doc) {
    const numero = await genererNumero(doc.type)
    const user   = await getCurrentUser()
    const { data, error } = await supabase
      .from('documents')
      .insert({ ...mapDocToDb(doc), numero, created_by: user.id, updated_by: user.id })
      .select()
      .single()
    if (error) throw error
    await log('CREATE', 'documents', data.id, null, data)
    return mapDocFromDb(data)
  },

  async update(id, doc) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('documents')
      .update({ ...mapDocToDb(doc), updated_by: user.id })
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    await log('UPDATE', 'documents', id, null, data)
    return mapDocFromDb(data)
  },

  async delete(id) {
    const { error } = await supabase.from('documents').delete().eq('id', id)
    if (error) throw error
    await log('DELETE', 'documents', id, null, null)
  },

  // Verrouillage optimiste : empêche deux utilisateurs d'éditer en même temps
  async lock(id) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('documents')
      .update({ locked_by: user.id, locked_at: new Date().toISOString() })
      .eq('id', id)
      .is('locked_by', null)
      .select('locked_by, locked_at')
      .maybeSingle()
    return !error && data !== null
  },

  async unlock(id) {
    const user = await getCurrentUser()
    await supabase
      .from('documents')
      .update({ locked_by: null, locked_at: null })
      .eq('id', id)
      .eq('locked_by', user.id)
  },

  // Temps réel — retourne le channel Supabase à unsubscribe en cleanup
  subscribe(callback) {
    return supabase
      .channel('documents-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'documents' }, callback)
      .subscribe()
  },
}

// ── Clients ───────────────────────────────────────────────────────────────────

export const clientsApi = {

  async liste({ search, actif = true } = {}) {
    let q = supabase.from('clients').select('*').order('nom')
    if (actif !== null) q = q.eq('actif', actif)
    if (search) q = q.or(
      `nom.ilike.%${search}%,email.ilike.%${search}%,telephone.ilike.%${search}%`
    )
    const { data, error } = await q
    if (error) throw error
    return data || []
  },

  async get(id) {
    const { data, error } = await supabase.from('clients').select('*').eq('id', id).single()
    if (error) throw error
    return data
  },

  async create(client) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('clients')
      .insert({ ...client, created_by: user.id, updated_by: user.id })
      .select().single()
    if (error) throw error
    await log('CREATE', 'clients', data.id, null, data)
    return data
  },

  async update(id, updates) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('clients')
      .update({ ...updates, updated_by: user.id })
      .eq('id', id).select().single()
    if (error) throw error
    return data
  },

  async delete(id) {
    const { error } = await supabase.from('clients').update({ actif: false }).eq('id', id)
    if (error) throw error
  },
}

// ── Fournisseurs ──────────────────────────────────────────────────────────────

export const fournisseursApi = {

  async liste({ search } = {}) {
    let q = supabase.from('fournisseurs').select('*').eq('actif', true).order('nom')
    if (search) q = q.ilike('nom', `%${search}%`)
    const { data, error } = await q
    if (error) throw error
    return data || []
  },

  async create(f) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('fournisseurs')
      .insert({ ...f, created_by: user.id, updated_by: user.id })
      .select().single()
    if (error) throw error
    return data
  },

  async update(id, updates) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('fournisseurs')
      .update({ ...updates, updated_by: user.id })
      .eq('id', id).select().single()
    if (error) throw error
    return data
  },

  async delete(id) {
    const { error } = await supabase.from('fournisseurs').update({ actif: false }).eq('id', id)
    if (error) throw error
  },
}

// ── Catalogue ─────────────────────────────────────────────────────────────────

export const catalogueApi = {

  async liste({ search, catMere, catFille, actif = true } = {}) {
    let q = supabase.from('catalogue').select('*').order('designation')
    if (actif !== null) q = q.eq('actif', actif)
    if (catMere  && catMere  !== 'toutes') q = q.eq('categorie_mere', catMere)
    if (catFille && catFille !== 'toutes') q = q.eq('categorie_fille', catFille)
    if (search) q = q.or(
      `designation.ilike.%${search}%,ref.ilike.%${search}%,code_article.ilike.%${search}%`
    )
    const { data, error } = await q
    if (error) throw error
    // ReMapper vers la forme attendue par l'app existante
    return (data || []).map(mapArticleFromDb)
  },

  async create(article) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('catalogue')
      .insert({ ...mapArticleToDb(article), created_by: user.id, updated_by: user.id })
      .select().single()
    if (error) throw error
    return mapArticleFromDb(data)
  },

  async update(id, article) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('catalogue')
      .update({ ...mapArticleToDb(article), updated_by: user.id })
      .eq('id', id).select().single()
    if (error) throw error
    return mapArticleFromDb(data)
  },

  async updateByRef(ref, article) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('catalogue')
      .update({ ...mapArticleToDb(article), updated_by: user.id })
      .eq('ref', ref).select().single()
    if (error) throw error
    return mapArticleFromDb(data)
  },

  async delete(ref) {
    const { error } = await supabase.from('catalogue').update({ actif: false }).eq('ref', ref)
    if (error) throw error
  },

  // Import initial du catalogue embarqué dans le HTML (à exécuter une seule fois)
  async importerBase(articles) {
    const chunkSize = 200
    for (let i = 0; i < articles.length; i += chunkSize) {
      const chunk = articles.slice(i, i + chunkSize)
      const { error } = await supabase
        .from('catalogue')
        .upsert(chunk.map(a => ({
          ...mapArticleToDb(a),
          is_base: true,
        })), { onConflict: 'ref', ignoreDuplicates: true })
      if (error) throw error
      // Petite pause pour ne pas saturer Supabase
      await new Promise(r => setTimeout(r, 50))
    }
  },

  getCategoriesMere: async () => {
    const { data } = await supabase
      .from('catalogue')
      .select('categorie_mere')
      .eq('actif', true)
      .not('categorie_mere', 'is', null)
    return [...new Set((data || []).map(r => r.categorie_mere).filter(Boolean))]
  },

  getCategoriesFille: async (catMere) => {
    let q = supabase.from('catalogue').select('categorie_fille').eq('actif', true)
    if (catMere && catMere !== 'toutes') q = q.eq('categorie_mere', catMere)
    const { data } = await q
    return [...new Set((data || []).map(r => r.categorie_fille).filter(Boolean))]
  },
}

// ── Bons de Commande ──────────────────────────────────────────────────────────

export const bonsCommandeApi = {

  async liste({ page = 1, perPage = 10, statut, search } = {}) {
    let q = supabase
      .from('bons_commande')
      .select('*, fournisseur:fournisseurs(nom)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range((page - 1) * perPage, page * perPage - 1)

    if (statut && statut !== 'all') q = q.eq('statut', statut)
    if (search) q = q.or(`numero.ilike.%${search}%,objet.ilike.%${search}%`)

    const { data, error, count } = await q
    if (error) throw error
    return { data: data || [], count: count || 0 }
  },

  async create(bc) {
    const numero = await genererNumero('bc')
    const user   = await getCurrentUser()
    const { data, error } = await supabase
      .from('bons_commande')
      .insert({ ...mapBCToDb(bc), numero, created_by: user.id, updated_by: user.id })
      .select().single()
    if (error) throw error
    return data
  },

  async update(id, bc) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('bons_commande')
      .update({ ...mapBCToDb(bc), updated_by: user.id })
      .eq('id', id).select().single()
    if (error) throw error
    return data
  },

  async delete(id) {
    const { error } = await supabase.from('bons_commande').delete().eq('id', id)
    if (error) throw error
  },
}

// ── Bordereaux de Livraison ───────────────────────────────────────────────────

export const bordereauxApi = {

  async liste() {
    const { data, error } = await supabase
      .from('bordereaux_livraison')
      .select('*, client:clients(nom)')
      .order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  },

  async create(bl) {
    const numero = await genererNumero('bl')
    const user   = await getCurrentUser()
    const { data, error } = await supabase
      .from('bordereaux_livraison')
      .insert({ ...bl, numero, created_by: user.id, updated_by: user.id })
      .select().single()
    if (error) throw error
    return data
  },

  async update(id, bl) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('bordereaux_livraison')
      .update({ ...bl, updated_by: user.id })
      .eq('id', id).select().single()
    if (error) throw error
    return data
  },

  async delete(id) {
    const { error } = await supabase.from('bordereaux_livraison').delete().eq('id', id)
    if (error) throw error
  },
}

// ── Settings ──────────────────────────────────────────────────────────────────

export const settingsApi = {

  async get() {
    const { data, error } = await supabase
      .from('settings').select('*').eq('id', 1).single()
    if (error) throw error
    return mapSettingsFromDb(data)
  },

  async update(settings) {
    const user = await getCurrentUser()
    const { data, error } = await supabase
      .from('settings')
      .update({ ...mapSettingsToDb(settings), updated_by: user.id })
      .eq('id', 1).select().single()
    if (error) throw error
    return mapSettingsFromDb(data)
  },
}

// ── Utilisateurs ──────────────────────────────────────────────────────────────

export const usersApi = {

  async liste() {
    const { data, error } = await supabase
      .from('profils').select('*').order('nom')
    if (error) throw error
    return data || []
  },

  async changerRole(userId, role) {
    const { data, error } = await supabase
      .from('profils').update({ role }).eq('id', userId).select().single()
    if (error) throw error
    return data
  },

  async desactiver(userId) {
    const { error } = await supabase
      .from('profils').update({ actif: false }).eq('id', userId)
    if (error) throw error
  },

  async supprimer(userId) {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const SERVICE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_KEY
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${SERVICE_KEY}`, 'apikey': SERVICE_KEY },
    })
    if (!resp.ok) {
      const text = await resp.text()
      let b = {}; try { b = JSON.parse(text) } catch {}
      throw new Error(b.msg || b.message || text || `Erreur ${resp.status}`)
    }
  },

  async inviter(email, nom, role, password) {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const SERVICE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_KEY
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SERVICE_KEY}`, 'apikey': SERVICE_KEY },
      body: JSON.stringify({ email, password, email_confirm: true, user_metadata: { nom, role, first_login: true } }),
    })
    const body = await resp.json()
    if (!resp.ok) throw new Error(body.msg || body.message || `Erreur ${resp.status}`)
    return body
  },

  async inviterParMail(email, nom, role) {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const SERVICE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_KEY
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SERVICE_KEY}`, 'apikey': SERVICE_KEY },
      body: JSON.stringify({ email, data: { nom, role } }),
    })
    const text = await resp.text()
    let body = {}
    try { body = text ? JSON.parse(text) : {} } catch { body = { msg: text } }
    if (!resp.ok) throw new Error(body.msg || body.message || text || `Erreur ${resp.status}`)
    return body
  },

  async reinitialiserMotDePasse(userId, password) {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const SERVICE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_KEY
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/admin/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SERVICE_KEY}`,
        'apikey': SERVICE_KEY,
      },
      body: JSON.stringify({ password }),
    })
    const body = await resp.json()
    if (!resp.ok) throw new Error(body.msg || body.message || `Erreur ${resp.status}`)
    return body
  },

  async mettreAJourPermissions(userId, permissionsCustom) {
    const { data, error } = await supabase
      .from('profils')
      .update({ permissions_custom: permissionsCustom })
      .eq('id', userId)
      .select()
      .single()
    if (error) throw error
    return data
  },
}

// ═════════════════════════════════════════════════════════════════════════════
// MAPPERS — conversion entre le format de l'app (camelCase) et la DB (snake_case)
// ═════════════════════════════════════════════════════════════════════════════

function mapDocToDb(doc) {
  return {
    type:              doc.type,
    statut:            doc.statut,
    date:              doc.date,
    date_echeance:     doc.dateEcheance   || doc.date_echeance,
    client_id:         doc.clientId       || doc.client_id,
    client_snapshot:   doc.client         || null,
    objet:             doc.objet,
    items:             doc.items          || [],
    sous_total_ht:     doc.sousTotal       ?? doc.sous_total_ht ?? 0,
    montant_tva:       doc.tva             ?? doc.montant_tva   ?? 0,
    total:             doc.total          || 0,
    tax_mode:          doc.taxMode         || doc.tax_mode       || 'TTC',
    remise_globale:    doc.remiseGlobale?.montant ?? doc.remiseGlobale?.valeur ?? 0,
    conditions:        doc.conditions,
    notes:             doc.notes,
    devis_origine_id:  doc.devisOrigineId  || doc.devis_origine_id,
    avoir_source_id:   doc.avoirSourceId   || doc.avoir_source_id,
    methode_paiement:  doc.methodePaiement || doc.methode_paiement,
    raison_annulation: doc.raisonAnnulation|| doc.raison_annulation,
    // Paiements partiels
    montant_avance:          doc.montantAvance          ?? 0,
    montant_paye:            doc.montantPaye            ?? 0,
    montant_restant:         doc.montantRestant         ?? doc.total ?? 0,
    afficher_mode_taxation:  doc.afficherModeTaxation   ?? false,
    // Données étendues JSONB
    paiements:               doc.paiements,
    frais_supplementaires:   doc.fraisSupplementaires,
    penalites_retard:        doc.penalitesRetard,
  }
}

function mapDocFromDb(row) {
  if (!row) return null
  return {
    id:                row.id,
    numero:            row.numero,
    type:              row.type,
    statut:            row.statut,
    date:              row.date,
    dateEcheance:      row.date_echeance,
    client:            row.client_snapshot || {},
    clientId:          row.client_id,
    objet:             row.objet,
    items:             row.items || [],
    sousTotal:         row.sous_total_ht,
    tva:               row.montant_tva,
    total:             row.total,
    taxMode:           row.tax_mode || 'TTC',
    remiseGlobale:     (() => {
      const totalFrais = (row.frais_supplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
      const totalHT = (row.total || 0) - (row.montant_tva || 0) - totalFrais;
      const montant = Math.max(0, Math.round(((row.sous_total_ht || 0) - totalHT) * 100) / 100);
      return { valeur: montant, type: montant > 0 ? 'montant' : 'aucune', montant };
    })(),
    conditions:        row.conditions,
    notes:             row.notes,
    devisOrigineId:    row.devis_origine_id,
    avoirSourceId:     row.avoir_source_id,
    methodePaiement:   row.methode_paiement,
    raisonAnnulation:  row.raison_annulation,
    paiements:              row.paiements || [],
    fraisSupplementaires:   row.frais_supplementaires || [],
    penalitesRetard:        row.penalites_retard || {},
    montantAvance:          row.montant_avance || 0,
    montantPaye:            (() => {
      const fromPaiements = (row.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
      if (fromPaiements > 0) return fromPaiements;
      if (row.montant_paye > 0) return row.montant_paye;
      return row.montant_avance || 0; // fallback ancien champ
    })(),
    montantRestant:         (() => {
      const fromPaiements = (row.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
      if (fromPaiements > 0) return Math.max(0, (row.total || 0) - fromPaiements);
      if (row.montant_restant > 0) return row.montant_restant;
      if (row.montant_avance > 0) return Math.max(0, (row.total || 0) - row.montant_avance);
      return row.total || 0;
    })(),
    afficherModeTaxation:   row.afficher_mode_taxation  || false,
    createdBy:              row.created_by,
    updatedAt:         row.updated_at,
    lockedBy:          row.locked_by,
    lockedAt:          row.locked_at,
    // Infos créateur (si joint)
    createur:          row.createur,
  }
}

function mapArticleToDb(a) {
  return {
    ref:                   a.ref,
    code_article:          a.codeArticle,
    designation:           a.designation,
    description:           a.description,
    categorie_mere:        a.categoreMere,
    code_categorie_mere:   a.codeCategorieMere,
    categorie_fille:       a.categorieFille,
    code_categorie_fille:  a.codeCategorieFille,
    marque:                a.marque,
    ref_fournisseur:       a.refFournisseur,
    unite:                 a.unite    || 'pièce',
    prix_unitaire:         a.prixUnitaire ?? 0,
    tva:                   a.tva      || 'TTC',
    actif:                 a.actif    !== false,
  }
}

function mapArticleFromDb(row) {
  if (!row) return null
  return {
    id:                row.id,
    ref:               row.ref,
    codeArticle:       row.code_article,
    designation:       row.designation,
    description:       row.description,
    categoreMere:      row.categorie_mere,
    codeCategorieMere: row.code_categorie_mere,
    categorieFille:    row.categorie_fille,
    codeCategorieFille:row.code_categorie_fille,
    marque:            row.marque,
    refFournisseur:    row.ref_fournisseur,
    unite:             row.unite        || 'pièce',
    prixUnitaire:      row.prix_unitaire ?? 0,
    tva:               row.tva          || 'TTC',
    actif:             row.actif,
    isBase:            row.is_base,
  }
}

function mapBCToDb(bc) {
  return {
    statut:               bc.statut,
    date:                 bc.date,
    date_livraison:       bc.dateLivraison,
    fournisseur_id:       bc.fournisseurId,
    fournisseur_snapshot: bc.fournisseur || null,
    objet:                bc.objet,
    items:                bc.items || [],
    sous_total_ht:        bc.sousTotal  ?? 0,
    montant_tva:          bc.tva        ?? 0,
    total:                bc.total      || 0,
    conditions:           bc.conditions,
    notes:                bc.notes,
    raison_annulation:    bc.raisonAnnulation,
  }
}

function mapSettingsToDb(s) {
  return {
    raison_sociale:            s.raisonSociale,
    adresse:                   s.adresse,
    telephone:                 s.telephone,
    email:                     s.email,
    site_web:                  s.siteWeb,
    nui:                       s.nui,
    rccm:                      s.rccm,
    logo:                      s.logo,
    agent:                     s.agent,
    modele_devis:              s.modeleDevis,
    modele_facture:            s.modeleFacture,
    modele_recu:               s.modeleRecu,
    delai_paiement_defaut:     s.delaiPaiementDefaut,
    taux_tva:                  s.tauxTVA,
    penalites_retard_actives:  s.penalitesRetardActives,
    taux_penalites_jour:       s.tauxPenalitesJour,
    message_penalites:         s.messagePenalites,
    cgv:                       s.cgv,
  }
}

function mapSettingsFromDb(row) {
  if (!row) return null
  return {
    raisonSociale:           row.raison_sociale,
    adresse:                 row.adresse,
    telephone:               row.telephone,
    email:                   row.email,
    siteWeb:                 row.site_web,
    nui:                     row.nui,
    rccm:                    row.rccm,
    logo:                    row.logo,
    agent:                   row.agent,
    modeleDevis:             row.modele_devis,
    modeleFacture:           row.modele_facture,
    modeleRecu:              row.modele_recu,
    delaiPaiementDefaut:     row.delai_paiement_defaut,
    tauxTVA:                 row.taux_tva,
    penalitesRetardActives:  row.penalites_retard_actives,
    tauxPenalitesJour:       row.taux_penalites_jour,
    messagePenalites:        row.message_penalites,
    cgv:                     row.cgv || { activer: true, position: 'footer', texte: '' },
  }
}
